import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CheckHeader } from '@/features/check/CheckHeader';
import { formatMoney } from '@/lib/money';
import { useDocumentTitle } from '@/lib/useDocumentTitle';
import { useTranslation } from '@/i18n';
import { isDiagnostic } from './wallets/sheetFailure';
import type { MessageKey } from '@/i18n';
import type { Check, Location, PaymentFailure, PaymentFailureCode } from '@/api/types';

interface PaymentFailureScreenProps {
    location: Location;
    check: Check;
    failure: PaymentFailure;

    /**
     * The browser's own account of a failure, for whoever is debugging one.
     *
     * Rendered only under `?debug=1`. A guest gets the sentence they can act on and never an exception name —
     * but a wallet sheet that refuses to open reports its reason nowhere else, and the people who hit it are
     * holding a phone with no console.
     */
    detail?: string;

    /** Rendered only when the server says another attempt is worth making. */
    onRetry?: () => void;
    onBackToCheck?: () => void;
}

/** The words come from the code. Extend this and the catalogue together. */
const FAILURE_COPY: Record<PaymentFailureCode, { title: MessageKey; detail: MessageKey }> = {
    declined: { title: 'failure.declined.title', detail: 'failure.declined.detail' },
    expiredCard: { title: 'failure.expiredCard.title', detail: 'failure.expiredCard.detail' },
    insufficientFunds: {
        title: 'failure.insufficientFunds.title',
        detail: 'failure.insufficientFunds.detail',
    },
    authenticationFailed: {
        title: 'failure.authenticationFailed.title',
        detail: 'failure.authenticationFailed.detail',
    },
    checkAlreadyPaid: { title: 'failure.checkAlreadyPaid.title', detail: 'failure.checkAlreadyPaid.detail' },
    checkChanged: { title: 'failure.checkChanged.title', detail: 'failure.checkChanged.detail' },
    gatewayUnavailable: {
        title: 'failure.gatewayUnavailable.title',
        detail: 'failure.gatewayUnavailable.detail',
    },
    expired: { title: 'failure.expired.title', detail: 'failure.expired.detail' },
};

const UNKNOWN_COPY = { title: 'failure.unknown.title', detail: 'failure.unknown.detail' } as const;

/**
 * What the guest sees when the money did not move.
 *
 * The counterpart to the receipt, and it answers the same first question in the opposite direction. Every
 * message that could plausibly leave a guest wondering says "nothing was charged" outright, because that is
 * the only thing they actually need in the moment — the rest is detail they will read if still worried.
 *
 * Two rules keep it honest, and they mirror the tip screen's split between what to say and what to do. The
 * CODE chooses the words. `retryable` chooses whether there is a retry button, and it comes from the server
 * rather than being inferred here: the server knows about a gateway outage or a card already refused twice,
 * and two implementations of "worth another go" would drift apart.
 *
 * An unrecognised code falls back to a generic message rather than rendering nothing. The server's list grows
 * without a client release, and a blank screen immediately after a failed payment is the worst possible
 * moment to discover a missing translation.
 */
export function PaymentFailureScreen({
    location,
    check,
    failure,
    detail,
    onRetry,
    onBackToCheck,
}: PaymentFailureScreenProps) {
    const { t } = useTranslation();
    const { currency, locale } = location;

    useDocumentTitle(t('document.titleAtLocation', { name: location.name }));

    const copy = FAILURE_COPY[failure.code] ?? UNKNOWN_COPY;

    return (
        <div className="min-h-dvh bg-muted">
            <div className="mx-auto w-full max-w-[var(--shell-max-width)]">
                <CheckHeader location={location} check={check} />

                <main className="flex flex-col gap-4 px-5 pb-12">
                    {/*
                      * `role="alert"` rather than the receipt's `status`: a payment that did not go through is
                      * assertive news, and a guest may well have looked away from the screen expecting it to
                      * finish on its own.
                      */}
                    <div
                        role="alert"
                        className="flex flex-col items-center gap-3 rounded-xl border border-border bg-card px-6 py-7 text-center shadow-xs"
                    >
                        <span className="flex size-14 items-center justify-center rounded-full bg-destructive text-destructive-foreground">
                            <AlertTriangle className="size-7" strokeWidth={2.5} aria-hidden="true" />
                        </span>

                        <h2 className="text-[1.375rem] leading-tight font-semibold tracking-tight text-balance">
                            {t(copy.title)}
                        </h2>
                        <p className="text-[0.9375rem] leading-relaxed text-muted-foreground text-pretty">
                            {t(copy.detail)}
                        </p>

                        {/*
                          * Not for guests. Under `?debug=1` only, because a wallet sheet that will not open
                          * says why to the browser console and to nobody else - and the person holding the
                          * phone cannot read one.
                          */}
                        {detail && isDiagnostic() && (
                            <p
                                data-testid="failure-detail"
                                className="mt-1 w-full border-t border-border pt-3 text-left font-mono text-[0.75rem] leading-relaxed break-words text-muted-foreground"
                            >
                                {detail}
                            </p>
                        )}

                        {/*
                          * The one failure where a number is the point. The check in context has already been
                          * refetched, so this is the figure they would be paying if they carried on.
                          */}
                        {failure.code === 'checkChanged' && (
                            <dl className="tabular mt-1 flex w-full items-baseline justify-between gap-3 border-t border-border pt-3.5 text-left">
                                <dt className="text-[0.9375rem] font-medium text-muted-foreground">
                                    {t('failure.checkChanged.newTotal')}
                                </dt>
                                <dd className="text-xl font-semibold tracking-tight">
                                    {formatMoney(check.totalCents, currency, locale)}
                                </dd>
                            </dl>
                        )}
                    </div>

                    <div className="flex flex-col gap-2.5">
                        {failure.retryable && onRetry && (
                            <Button
                                size="lg"
                                className="h-[3.25rem] w-full rounded-xl text-base font-semibold"
                                onClick={onRetry}
                            >
                                {t('failure.retry')}
                            </Button>
                        )}

                        {onBackToCheck && (
                            <Button
                                variant="outline"
                                size="lg"
                                className="h-[3.25rem] w-full rounded-xl text-base font-semibold"
                                onClick={onBackToCheck}
                            >
                                {t('failure.backToCheck')}
                            </Button>
                        )}
                    </div>
                </main>
            </div>
        </div>
    );
}
